const STORAGE_CLIENT = 'qr_app_client_guid';
const STORAGE_APP = 'qr_app_application_guid';

/** Public client id in `/{id}` (domain root path): exactly 5 letters and digits (e.g. 324ue). Same as backend `isClientPublicId`. */
const CLIENT_PUBLIC_ID_RE = /^[a-z0-9]{5}$/i;

/** Valid client public id for welcome URL and API (not the application UUID). */
export function isValidClientGuidFormat(id: string | undefined | null): boolean {
  if (!id) return false;
  return CLIENT_PUBLIC_ID_RE.test(id.trim());
}

function guidFromSearch(searchParams: URLSearchParams): string | null {
  const raw = searchParams.get('ClientId') ?? '';
  const id = raw.trim();
  if (!id || !CLIENT_PUBLIC_ID_RE.test(id)) return null;
  return id;
}

export function resolveClientGuid(
  pathname: string,
  searchParams: URLSearchParams
): string | null {
  const trimmed = pathname.replace(/\/+$/, '') || '/';

  // /{client_uid} at site root (reserved single-segment routes like /applications fail the id regex)
  const rootSeg = trimmed.match(/^\/([^/]+)$/i);
  if (rootSeg) {
    const id = rootSeg[1].trim();
    if (CLIENT_PUBLIC_ID_RE.test(id)) {
      sessionStorage.setItem(STORAGE_CLIENT, id);
      return id;
    }
  }

  // Legacy /welcome/{id}
  const legacyWelcome = trimmed.match(/^\/welcome\/([^/]+)$/i);
  if (legacyWelcome) {
    const id = legacyWelcome[1].trim();
    if (CLIENT_PUBLIC_ID_RE.test(id)) {
      sessionStorage.setItem(STORAGE_CLIENT, id);
      return id;
    }
    sessionStorage.removeItem(STORAGE_CLIENT);
    return null;
  }

  if (trimmed === '/' || trimmed === '/welcome') {
    const fromQuery = guidFromSearch(searchParams);
    if (fromQuery) {
      sessionStorage.setItem(STORAGE_CLIENT, fromQuery);
      return fromQuery;
    }
    sessionStorage.removeItem(STORAGE_CLIENT);
    return null;
  }

  const existing = sessionStorage.getItem(STORAGE_CLIENT);
  if (existing && CLIENT_PUBLIC_ID_RE.test(existing)) {
    return existing;
  }
  return null;
}

export function getClientGuidFromStorage(): string | null {
  const v = sessionStorage.getItem(STORAGE_CLIENT);
  if (v && CLIENT_PUBLIC_ID_RE.test(v)) return v;
  return null;
}

export function getApplicationGuidFromStorage(): string | null {
  return sessionStorage.getItem(STORAGE_APP);
}

export function setApplicationGuidInStorage(guid: string): void {
  sessionStorage.setItem(STORAGE_APP, guid);
}

export function clearApplicationGuidFromStorage(): void {
  sessionStorage.removeItem(STORAGE_APP);
}

/** Clear URL-derived client id from session */
export function clearClientGuidFromStorage(): void {
  sessionStorage.removeItem(STORAGE_CLIENT);
}
